import { z } from 'zod/v4'

const daysInRecurringMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

const isRecurringDate = (month: number, day: number) => {
  const daysInMonth = daysInRecurringMonth[month - 1]
  return (
    Number.isInteger(month) &&
    Number.isInteger(day) &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    daysInMonth !== undefined &&
    day <= daysInMonth
  )
}

export const seasonalDesignParamsSchema = z.object({
  seasonalDesignId: z.coerce.number().int().positive(),
})

export const seasonalDesignSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  seasonal_tag_id: z.coerce.number().int().positive('Choose an occasion or season'),
  photo_asset_id: z.coerce.number().int().positive('Choose a stock banner image'),
  hero: z.object({
    title: z.string().trim().max(160),
    subtext: z.string().max(500).nullable().optional(),
    buttonText: z.string().max(80).nullable().optional(),
    layout: z.enum(['classic', 'split', 'professional']),
    imageFocalPointX: z.coerce.number().min(0).max(100).nullable(),
    imageFocalPointY: z.coerce.number().min(0).max(100).nullable(),
    imageAltText: z.string().max(240).nullable().optional(),
    preferredTextPosition: z.enum(['left', 'center', 'right']),
    preferredTextTone: z.enum(['auto', 'light', 'dark']),
  }),
  sort_order: z.coerce.number().int().min(0).max(100000),
})

export const seasonalScheduleSchema = z
  .object({
    timing_mode: z.enum(['occasion_offset', 'annual_range']),
    days_before: z.coerce.number().int().min(0).max(90).optional(),
    days_after: z.coerce.number().int().min(0).max(90).optional(),
    start_month: z.coerce.number().int().min(1).max(12).optional(),
    start_day: z.coerce.number().int().min(1).max(31).optional(),
    end_month: z.coerce.number().int().min(1).max(12).optional(),
    end_day: z.coerce.number().int().min(1).max(31).optional(),
    end_is_last_day: z.boolean(),
    provision_new_organizations: z.boolean(),
    default_enabled: z.boolean(),
  })
  .superRefine((value, context) => {
    if (value.timing_mode === 'occasion_offset') {
      if (value.days_before == null) {
        context.addIssue({ code: 'custom', path: ['days_before'], message: 'Required' })
      }
      if (value.days_after == null) {
        context.addIssue({ code: 'custom', path: ['days_after'], message: 'Required' })
      }
      return
    }

    for (const field of ['start_month', 'start_day', 'end_month'] as const) {
      if (value[field] == null) {
        context.addIssue({ code: 'custom', path: [field], message: 'Required' })
      }
    }
    if (!value.end_is_last_day && value.end_day == null) {
      context.addIssue({ code: 'custom', path: ['end_day'], message: 'Required' })
    }
    if (
      value.start_month != null &&
      value.start_day != null &&
      !isRecurringDate(value.start_month, value.start_day)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['start_day'],
        message: 'Choose a date that occurs every year',
      })
    }
    if (
      !value.end_is_last_day &&
      value.end_month != null &&
      value.end_day != null &&
      !isRecurringDate(value.end_month, value.end_day)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['end_day'],
        message: 'Choose a date that occurs every year',
      })
    }
  })

const seasonalScheduleRowSchema = seasonalScheduleSchema.safeExtend({
  id: z.coerce.number().int().positive().optional(),
  seasonal_tag_id: z.coerce.number().int().positive('Choose an occasion or season').optional(),
})

export const seasonalScheduleCreateSchema = seasonalScheduleSchema.safeExtend({
  seasonal_tag_id: z.coerce.number().int().positive('Choose an occasion or season'),
})

export const seasonalSchedulesSchema = z.object({
  profiles: z.array(seasonalScheduleRowSchema),
  new_profiles: z.array(seasonalScheduleCreateSchema),
})
